import type { TFunction } from 'i18next';
import type { OrganizationStatusesByProjectsAndBoards } from '../../../../api/organization-client/organization-client.type';
import type { Step, Workflow } from '../../../../api/workflows-client/workflows-client.type';
import { STEP_NAME_TO_PHASE_KEY } from '../../../../helpers/phase-labels/phase-labels.type';
import type { ResolvedPhaseLabels } from '../../../../helpers/phase-labels/phase-labels.type';
import { normalizeStatusName } from '../../../../helpers/subgroup-helpers/subgroup-helpers';

/**
 * Gets the map of step names to their translated values
 * Evaluated at runtime to support language changes
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Record<string, string>} Map of step names to translated display names
 */
const getStepI18nMap = (t: TFunction): Record<string, string> => ({
  Backlog: t('common.workflow_steps.backlog'),
  'In Progress': t('common.workflow_steps.in_progress'),
  'In Review': t('common.workflow_steps.in_review'),
  'In Test': t('common.workflow_steps.in_test'),
  Deployable: t('common.workflow_steps.deployable'),
  Done: t('common.workflow_steps.done'),
  Waiting: t('common.workflow_steps.waiting'),
});

/**
 * Gets the display name for a workflow step, preferring the per-workflow
 * `bucket_label_overrides` (resolved via `external_section_map.labels`)
 * when supplied, falling back to the i18n translation, then to the raw
 * step name.
 *
 * Responsibilities:
 * 1. Translate the historic step name (e.g. "Waiting") to its canonical
 *    phase key via `STEP_NAME_TO_PHASE_KEY`.
 * 2. Read the override from `resolvedLabels` for that key, returning the
 *    override verbatim when present and non-empty.
 * 3. Fall back to `t('common.workflow_steps.*')` so historic behavior is
 *    preserved for workflows without overrides or for callers that don't
 *    pass `resolvedLabels`.
 *
 * @param {string} stepName - The historic step name (e.g. "Backlog",
 *   "In Progress", "Waiting").
 * @param {ResolvedPhaseLabels} [resolvedLabels] - Optional canonical-keyed
 *   map sourced from the workflow's `external_section_map.labels`.
 * @returns {string} The display name for the step.
 */
function getStepDisplayName(stepName: string, t: TFunction, resolvedLabels?: ResolvedPhaseLabels): string {
  if (resolvedLabels) {
    const phaseKey = STEP_NAME_TO_PHASE_KEY[stepName];
    if (phaseKey) {
      const override = resolvedLabels[phaseKey];
      if (typeof override === 'string') {
        const trimmedOverride = override.trim();
        if (trimmedOverride) {
          return trimmedOverride;
        }
      }
    }
  }
  const stepMap = getStepI18nMap(t);
  return stepMap[stepName] || stepName;
}

/**
 * Gets the statuses for the selected boards
 *
 * De-duplicates case-insensitively so two boards that report the same status
 * in different casing contribute a single entry. The first-seen spelling is
 * kept; both callers pass the result to
 * `maybeReplaceMappedStatusesFromWorkflowSteps` as a retention allow-list, so
 * which spelling survives affects nothing beyond that membership test.
 *
 * @param {OrganizationStatusesByProjectsAndBoards} OrganizationStatusesByProjectsAndBoards The portfolio statuses by projects and boards
 * @param {string[]} selectedBoards The selected boards
 * @returns {string[]} The statuses for the selected boards
 */
function getStatusesForSelectedBoards(
  OrganizationStatusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards,
  selectedBoards: string[],
): string[] {
  const statuses = Object.entries(OrganizationStatusesByProjectsAndBoards)
    .map(([_, project]) => project.subprojects)
    .flatMap((subprojects) => Object.entries(subprojects))
    .filter(([subprojectId, _]) => selectedBoards.includes(subprojectId))
    .flatMap(([_, subproject]) => subproject.statuses);

  const seenNormalizedStatuses = new Set<string>();
  const uniqueStatuses: string[] = [];
  statuses.forEach((status) => {
    const normalizedStatus = normalizeStatusName(status);
    if (seenNormalizedStatuses.has(normalizedStatus)) {
      return;
    }
    seenNormalizedStatuses.add(normalizedStatus);
    uniqueStatuses.push(status);
  });
  return uniqueStatuses;
}

/**
 * Gets the mapped and unmapped statuses for a subproject
 *
 * Compares case-insensitively because a task's stored status and the workflow
 * mapping's `external_name` may differ in casing from the board's status list
 * during and after the source-casing backfill. Returned strings keep their
 * original casing.
 *
 * @param {string[]} allStatusesForSubproject The list of all statuses for the subproject
 * @param {Record<string, string[]>} workflowMappedStatusBuckets The list of mapped statuses for the workflow
 * @returns {Object} The mapped and unmapped statuses
 */
function getMappedAndUnmappedStatusesForSubproject(
  allStatusesForSubproject: string[],
  workflowMappedStatusBuckets: { [key: string]: string[] },
): { mappedStatuses: string[]; unmappedStatuses: string[] } {
  const allNormalizedStatuses = new Set(allStatusesForSubproject.map(normalizeStatusName));
  const flattenedMappedStatuses = Object.values(workflowMappedStatusBuckets).flat();
  const normalizedMappedStatuses = new Set(flattenedMappedStatuses.map(normalizeStatusName));

  const mappedStatuses = flattenedMappedStatuses.filter((status) =>
    allNormalizedStatuses.has(normalizeStatusName(status)),
  );
  const unmappedStatuses = allStatusesForSubproject.filter(
    (status) => !normalizedMappedStatuses.has(normalizeStatusName(status)),
  );

  return { mappedStatuses, unmappedStatuses };
}

/**
 * Gets the new workflow name
 *
 * @param {Workflow[]} workflows The list of workflows
 * @returns {string} The new workflow name
 */
function getNewWorkflowName(workflows: Workflow[]): string {
  const matches = workflows.filter((workflow) => workflow && workflow.name.match(/^New Workflow(?:\s*\((\d+)\))?$/));
  if (matches.length > 0) {
    //sort the matches and pull the number from the last match using regex
    const sortedMatches = matches.sort();
    const lastMatch = sortedMatches[sortedMatches.length - 1];
    const lastMatchNumber = parseInt(lastMatch.name.match(/(\d+)/)?.[0] || (sortedMatches.length - 1).toString(), 10);
    return `New Workflow (${lastMatchNumber + 1})`;
  } else {
    return 'New Workflow';
  }
}

/**
 * Gets the mapped and unmapped statuses for a workflow.
 *
 * Membership is tested on the normalized (lowercased, whitespace-collapsed)
 * status name, so a mapping persisted as `"in progress"` still counts the
 * board's `"In Progress"` as mapped once that board is backfilled to source
 * casing. Without this, backfilling a board would silently un-map every
 * status on it. The returned strings keep the board's casing.
 *
 * @param workflow - The workflow to get the mapped and unmapped statuses for.
 * @param statusesByProjectsAndBoards - The portfolio statuses by projects and boards.
 * @returns An object with the mapped and unmapped statuses for the workflow.
 */
function getMappedAndUnmappedStatusesForWorkflow(
  workflow: Workflow,
  statusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards | undefined,
): { mappedStatuses: string[]; unmappedStatuses: string[] } {
  if (!statusesByProjectsAndBoards) {
    return { mappedStatuses: [], unmappedStatuses: [] };
  }
  // `external_name` is typed as required but arrives from drag payloads and
  // persisted rows, so a falsy value is reachable at runtime — and
  // `normalizeStatusName` would throw on it, taking the whole workflow map
  // down. Guarded the same way `maybeReplaceMappedStatusesFromWorkflowSteps`
  // already guards.
  const workflowMappings = new Set(
    (workflow.steps || []).flatMap((step) =>
      step.mappings
        .filter((mapping) => mapping.external_name)
        .map((mapping) => normalizeStatusName(mapping.external_name)),
    ),
  );

  // Keyed on the normalized name so two boards reporting the same status in
  // different casing collapse to one entry. Without this the workflow map
  // renders two chips whose labels resolve identically, and dragging either
  // one makes both disappear.
  const mappedStatusesByKey = new Map<string, string>();
  const unmappedStatusesByKey = new Map<string, string>();

  for (const [_projectId, project] of Object.entries(statusesByProjectsAndBoards)) {
    for (const [_subprojectId, subproject] of Object.entries(project.subprojects)) {
      for (const status of subproject.statuses) {
        const normalizedStatus = normalizeStatusName(status);
        const target = workflowMappings.has(normalizedStatus) ? mappedStatusesByKey : unmappedStatusesByKey;
        if (!target.has(normalizedStatus)) {
          target.set(normalizedStatus, status);
        }
      }
    }
  }
  return {
    mappedStatuses: Array.from(mappedStatusesByKey.values()),
    unmappedStatuses: Array.from(unmappedStatusesByKey.values()),
  };
}

/**
 * Filters the portfolio statuses by projects and boards by a list of subproject ids
 *
 * @param {OrganizationStatusesByProjectsAndBoards} OrganizationStatusesByProjectsAndBoards The portfolio statuses by projects and boards
 * @param {string[]} subprojectIds The subproject ids to filter by
 * @returns {OrganizationStatusesByProjectsAndBoards} The portfolio statuses by projects and boards filtered by subproject ids
 */
function filterOrganizationStatusesByProjectsAndBoardsBySubprojectIds(
  OrganizationStatusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards | undefined,
  subprojectIds: string[],
): OrganizationStatusesByProjectsAndBoards | undefined {
  if (!OrganizationStatusesByProjectsAndBoards) {
    return undefined;
  }
  const result: OrganizationStatusesByProjectsAndBoards = {};
  for (const [projectId, project] of Object.entries(OrganizationStatusesByProjectsAndBoards)) {
    const filteredSubprojectData = Object.fromEntries(
      Object.entries(project.subprojects).filter(([subprojectId]) => (subprojectIds || []).includes(subprojectId)),
    );
    if (Object.keys(filteredSubprojectData).length > 0) {
      result[projectId] = {
        ...project,
        subprojects: filteredSubprojectData,
      };
    }
  }
  return result;
}
/**
 * Filters the workflow steps to only include the mapped statuses that are in the statusOptions array
 *
 * Retention is decided case-insensitively so a casing change on the board does
 * not drop an existing mapping. Retained mappings are left untouched, keeping
 * `external_name` byte-identical to what the backend stores.
 *
 * @param {Workflow} workflow The workflow to filter
 * @param {string[]} statusOptionsToRetain The statuses to retain in the workflow steps
 * @returns {Step[]} The workflow steps with any extra mapped statuses removed
 */
function maybeReplaceMappedStatusesFromWorkflowSteps(workflow: Workflow, statusOptionsToRetain: string[]): Step[] {
  const { steps = [] } = workflow;
  const normalizedStatusesToRetain = new Set(statusOptionsToRetain.map(normalizeStatusName));
  const newSteps: Step[] = [];
  steps.forEach((step) => {
    const newMappings = step.mappings.filter(
      (mapping) => mapping.external_name && normalizedStatusesToRetain.has(normalizeStatusName(mapping.external_name)),
    );
    newSteps.push({ ...step, mappings: newMappings });
  });
  return newSteps;
}

/**
 * Filters the workflow steps to only include the mapped statuses that are not in the statusOptions array
 *
 * Removal is decided case-insensitively so dragging a status out of a step
 * clears the existing mapping even when the drag payload's casing differs from
 * the persisted `external_name`. Without this, a moved status would end up
 * mapped to two steps at once.
 *
 * Note that `normalizeStatusName` also collapses internal whitespace, so
 * `"In  Progress"` and `"In Progress"` are treated as one status and removed
 * together. That is intentional — the workflow map shows a single chip per
 * normalized status, so removing that chip must clear every mapping it
 * represents or the UI would report a status as unmapped while a hidden
 * mapping survived.
 *
 * @param {Workflow} workflow The workflow to filter
 * @param {string[]} statusOptionsToRemove The statuses to remove from the workflow steps
 * @returns {Step[]} The workflow steps with any extra mapped statuses removed
 */
function maybeRemoveMappedStatusesFromWorkflowSteps(workflow: Workflow, statusOptionsToRemove: string[]): Step[] {
  const { steps = [] } = workflow;
  const normalizedStatusesToRemove = new Set(statusOptionsToRemove.map(normalizeStatusName));
  const newSteps: Step[] = [];

  steps.forEach((step) => {
    const newMappings = step.mappings.filter(
      (mapping) =>
        !mapping.external_name || !normalizedStatusesToRemove.has(normalizeStatusName(mapping.external_name)),
    );
    newSteps.push({ ...step, mappings: newMappings });
  });

  return newSteps;
}

/**
 * Adds a step to an array of steps and sorts them according to a specified order
 *
 * @param {Step[]} steps - The existing array of workflow steps
 * @param {Step} step - The new step to add
 * @param {string[]} stepOrder - Array defining the desired order of step names
 * @returns {Step[]} New array of steps with the added step, sorted by the stepOrder
 */

function addStepToSteps(steps: Step[], step: Step, stepOrder: string[]): Step[] {
  const newSteps = [...steps.filter((s) => s.name !== step.name), step];

  return newSteps.sort((a, b) => stepOrder.indexOf(a.name) - stepOrder.indexOf(b.name));
}

export {
  addStepToSteps,
  filterOrganizationStatusesByProjectsAndBoardsBySubprojectIds,
  getMappedAndUnmappedStatusesForSubproject,
  getMappedAndUnmappedStatusesForWorkflow,
  getNewWorkflowName,
  getStatusesForSelectedBoards,
  getStepDisplayName,
  maybeRemoveMappedStatusesFromWorkflowSteps,
  maybeReplaceMappedStatusesFromWorkflowSteps,
};
